'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { SHOW_BLOG_POST_DATES } from '@/lib/blog-display';

export interface BlogCardItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImageUrl: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  publishedDate: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/** Build a /blog href preserving the active tag while switching category. */
function buildHref(params: { category?: string | null; tag?: string | null; page?: number }): string {
  const qs = new URLSearchParams();
  if (params.category) qs.set('category', params.category);
  if (params.tag) qs.set('tag', params.tag);
  if (params.page && params.page > 1) qs.set('page', String(params.page));
  const q = qs.toString();
  return q ? `/blog?${q}` : '/blog';
}

export function BlogIndexContent({
  items,
  categories,
  activeCategory,
  activeTag,
  page,
  totalPages,
  hasMore,
}: {
  items: BlogCardItem[];
  categories: { id: string; name: string; slug: string }[];
  activeCategory: string | null;
  activeTag: string | null;
  page: number;
  totalPages: number;
  hasMore: boolean;
}) {
  const t = useTranslations('public.blog');

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-eifo-primary">{t('heading')}</h1>
        <p className="mt-2 text-muted-foreground">{t('subheading')}</p>
      </header>

      {/* Category filter chips */}
      {categories.length > 0 && (
        <nav className="mb-6 flex flex-wrap gap-2" aria-label={t('filterByCategory')}>
          <Link
            href={buildHref({ tag: activeTag })}
            className={`rounded-full border px-3 py-1 text-sm ${
              !activeCategory
                ? 'bg-eifo-primary text-white border-eifo-primary'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('allCategories')}
          </Link>
          {categories.map((c) => (
            <Link
              key={c.id}
              href={buildHref({ category: c.slug, tag: activeTag })}
              className={`rounded-full border px-3 py-1 text-sm ${
                activeCategory === c.slug
                  ? 'bg-eifo-primary text-white border-eifo-primary'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {c.name}
            </Link>
          ))}
        </nav>
      )}

      {activeTag && (
        <div className="mb-6 text-sm text-muted-foreground">
          {t('filterByTag')}: <span className="font-medium">#{activeTag}</span>{' '}
          <Link href={buildHref({ category: activeCategory })} className="text-eifo-primary underline">
            {t('clearFilter')}
          </Link>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-xl border bg-card py-16 text-center text-muted-foreground">
          {t('empty')}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((post) => (
            <Link
              key={post.id}
              href={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow hover:shadow-md"
            >
              {post.featuredImageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={post.featuredImageUrl}
                  alt={post.title}
                  className="aspect-[16/9] w-full object-cover"
                />
              ) : (
                <div className="aspect-[16/9] w-full bg-eifo-primary/5" />
              )}
              <div className="flex flex-1 flex-col p-5">
                {post.categoryName && (
                  <span className="mb-2 text-xs font-semibold uppercase tracking-wide text-eifo-primary">
                    {post.categoryName}
                  </span>
                )}
                <h2 className="text-lg font-semibold text-gray-900 group-hover:text-eifo-primary">
                  {post.title}
                </h2>
                {post.excerpt && (
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{post.excerpt}</p>
                )}
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  {/* Empty span keeps "Read more" right-aligned while dates are hidden. */}
                  <span>{SHOW_BLOG_POST_DATES ? formatDate(post.publishedDate) : ''}</span>
                  <span className="font-medium text-eifo-primary">{t('readMore')}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {(page > 1 || hasMore) && (
        <div className="mt-10 flex items-center justify-center gap-4">
          {page > 1 ? (
            <Link
              href={buildHref({ category: activeCategory, tag: activeTag, page: page - 1 })}
              className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
            >
              {t('prev')}
            </Link>
          ) : (
            <span className="rounded-lg border px-4 py-2 text-sm text-gray-300">{t('prev')}</span>
          )}
          <span className="text-sm text-muted-foreground">
            {t('pageOf', { page, total: totalPages })}
          </span>
          {hasMore ? (
            <Link
              href={buildHref({ category: activeCategory, tag: activeTag, page: page + 1 })}
              className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
            >
              {t('next')}
            </Link>
          ) : (
            <span className="rounded-lg border px-4 py-2 text-sm text-gray-300">{t('next')}</span>
          )}
        </div>
      )}
    </div>
  );
}
